import { OpportunitySchemaUtil } from '@features/opportunities/modules/core/utils/opportunity-schema-util';

import { OpportunitySchema } from '@features/opportunities/modules/core/models/schema-property-definition';
import { Applicant } from './applicant';
import { BaseApplicant } from './base-applicant';
import { SharePercentage } from './share-percentage.contract';

export namespace TrustApplicant {
  export enum TrustType {
    Smsf = 'SMSF trust',
    PropertyBare = 'Property / bare trust',
    Family = 'Family trust',
    Discretionary = 'Discretionary trust',
    Unit = 'Unit trust'
  }
  export const commonTrustTypes = [
    TrustType.Family,
    TrustType.Discretionary,
    TrustType.Unit
  ];

  export const smsfTrustTypes = [TrustType.Smsf, TrustType.PropertyBare];
  export interface SchemaPropertyDefinition
    extends BaseApplicant.SchemaPropertyDefinition {
    businessNumber: OpportunitySchema.StringPropertyDefinition;
    foundationDate: OpportunitySchema.StringPropertyDefinition;
    jurisdiction: OpportunitySchema.StringPropertyDefinition;
    managerName: OpportunitySchema.StringPropertyDefinition;
    name: OpportunitySchema.StringPropertyDefinition;
    settlorName: OpportunitySchema.StringPropertyDefinition;
    trustType: OpportunitySchema.StringPropertyDefinition;
    trustees: OpportunitySchema.StringArrayPropertyDefinition;
  }
  export interface SchemaContract extends BaseApplicant.SchemaContract {
    businessNumber?: string | null;
    foundationDate?: string | null;
    jurisdiction?: string | null;
    managerName?: string | null;
    name?: string | null;
    settlorName?: string | null;
    trustType?: string | null;
    trustees?: string[] | null;
    beneficiaries?: SharePercentage.Collection.SchemaContract;
  }
  export interface Model extends BaseApplicant.Model {
    id: string;
    businessNumber: string | null;
    foundationDate: string | null;
    jurisdiction: string | null;
    managerName: string | null;
    name: string | null;
    settlorName: string | null;
    trustType: TrustType | null;
    trustees: string[];
    beneficiaries: SharePercentage.Collection.Model;
  }

  export function toModel(id: string, c: SchemaContract): Model {
    return {
      id,
      role: c.role || '',
      businessNumber: c.businessNumber || null,
      foundationDate: c.foundationDate || null,
      jurisdiction: c.jurisdiction || null,
      managerName: c.managerName || null,
      name: c.name || null,
      settlorName: c.settlorName || null,
      trustType: (c.trustType as TrustType) || null,
      trustees: c.trustees || [],
      beneficiaries: SharePercentage.Collection.toModel(c.beneficiaries || {})
    };
  }

  export function toSchemaContract(c: Partial<Model>): Partial<SchemaContract> {
    return {
      ...c,
      foundationDate: OpportunitySchemaUtil.Transform.Date.toSchemaContract(
        c.foundationDate
      ),
      beneficiaries: c.beneficiaries
        ? SharePercentage.Collection.toSchemaContract(c.beneficiaries)
        : undefined
    };
  }

  export function toApplicantSummary(
    id: string,
    c: SchemaContract
  ): Applicant.Summary {
    return Model.toApplicantSummary(toModel(id, c));
  }

  export function getEligibleTrustees(
    applicants: Applicant.Summary[]
  ): Applicant.Summary[] {
    return applicants.filter(
      (a) => a.role !== Applicant.Role.General.Guarantor_Security_Only
    );
  }

  export function getEligibleBeneficiaries(
    applicants: Applicant.Summary[]
  ): Applicant.Summary[] {
    return applicants.filter(
      (a) => a.role !== Applicant.Role.General.Guarantor_Security_Only
    );
  }

  export namespace Model {
    export function toApplicantSummary(
      model: TrustApplicant.Model
    ): Applicant.Summary {
      return {
        id: `trusts/${model.id}`,
        entityId: model.id,
        displayName: model.name || '',
        role: model.role ?? '',
        applicantType:
          model.trustType == TrustType.Smsf
            ? Applicant.Type.SmsfTrust
            : model.trustType == TrustType.PropertyBare
            ? Applicant.Type.BareTrust
            : Applicant.Type.Trust,
        contactNumber: '',
        description: `${model.trustType || ''}`.trim(),
        email: null
      };
    }
  }
}
